import { Fragment, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import type { FeedPost } from "@gooner/shared";
import { track } from "../../analytics";
import { api } from "../../lib/api";
import { feedEntryKey, readFeedSnapshot, saveFeedSnapshot } from "../../lib/feedRestore";
import { GuideInfoButton, SurfaceGuide, useSurfaceGuide } from "../../components/SurfaceGuide";
import { FeedComposer } from "../components/feed/FeedComposer";
import { PostCard } from "../components/feed/PostCard";
import { Leaderboards } from "../components/feed/Leaderboards";
import { Button } from "../ui/primitives";
import { useOnlineRetry } from "../hooks/useOnlineRetry";
import { AdSlot } from "../components/AdSlot";

/**
 * THE FEED — /feed and /feed/:id (2026-09-13). The first social surface: a member posts
 * one clip with a line, others crown it, comment, reply. Two columns on a desktop (the
 * views page's shape): the composer and the posts, and beside them today's boards. On
 * a phone the boards follow the posts.
 *
 * Every post is a clip. There is nothing here to read without something to watch.
 */
export function Feed() {
  // One mount per history entry: /feed -> /feed/:id -> Back is the SAME component slot to
  // the router, and a kept instance would never re-read what its entry left behind.
  return <FeedPage key={useLocation().key} />;
}

function FeedPage() {
  const { id } = useParams<{ id?: string }>();
  /*
   * BACK RETURNS TO THE PLACE (lib/feedRestore). The list view (not one post's thread)
   * starts from what this history entry left behind — the loaded pages, the Load more
   * cursor, the scroll offset — instead of a fresh first page.
   */
  const loc = useLocation();
  const entryKey = feedEntryKey(loc.key, window.history.state);
  const [restored] = useState(() => (id ? null : readFeedSnapshot(entryKey)));
  // Coming BACK to the Feed is not arriving: no intro popup over a restored place.
  const guide = useSurfaceGuide("feed", restored === null);
  const [posts, setPosts] = useState<FeedPost[] | null>(restored?.posts ?? null);
  const [nextBefore, setNextBefore] = useState<number | undefined>(restored ? restored.nextBefore : undefined);
  const [error, setError] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      if (id) {
        const r = await api.feedPost(id);
        setPosts([r.post]);
        setNextBefore(undefined);
      } else {
        const r = await api.feed();
        setPosts(r.posts);
        setNextBefore(r.nextBefore);
        track("feed_opened", { posts: r.posts.length });
      }
    } catch (err) {
      const status = err instanceof Error ? Number(/^(\d{3})/.exec(err.message)?.[1]) : 0;
      setPosts([]);
      setError(status === 503 ? "The Feed isn't open yet." : status === 404 ? "That post is gone." : "Couldn't load the Feed.");
    }
  }, [id]);
  useEffect(() => {
    // A restored Feed already has its pages. NOT a consume-once flag: React StrictMode (dev)
    // runs this effect twice on one mount, and a flag cleared by the first run let the second
    // fetch a fresh first page over the restored 40 (20 posts parked at the old offset).
    // `restored` is fixed for the life of this instance (one mount per history entry).
    if (restored) return;
    void load();
  }, [load, restored]);

  // Put the reader back, before the first paint so there is no flash of the top. The posts
  // are already in this first render, so the page is tall enough to scroll to.
  useLayoutEffect(() => {
    if (restored && restored.scrollY > 0) window.scrollTo(0, restored.scrollY);
  }, [restored]);

  // Leave the place behind. The offset is the last one a scroll event reported, not read at
  // unmount: by then the next page may already have shortened the document and clamped it.
  // No event yet (a fresh entry left unscrolled) means null: read the real window offset at
  // save time, which is 0 here, never a leftover from another entry.
  const scrollYRef = useRef<number | null>(restored?.scrollY ?? null);
  const latest = useRef({ posts, nextBefore });
  latest.current = { posts, nextBefore };
  useLayoutEffect(() => {
    if (id) return;
    // Save now, from refs: never from a closure, and never a not-yet-loaded (null) list.
    const save = () => {
      const cur = latest.current;
      if (cur.posts && cur.posts.length > 0)
        saveFeedSnapshot(entryKey, { posts: cur.posts, nextBefore: cur.nextBefore, scrollY: scrollYRef.current ?? window.scrollY });
    };
    let timer: number | undefined;
    const onScroll = () => {
      scrollYRef.current = window.scrollY;
      // Throttled save while scrolling, so a fast Back (or a crash of the cleanup) still finds it.
      if (timer === undefined)
        timer = window.setTimeout(() => {
          timer = undefined;
          save();
        }, 250);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    // The browser's own scroll restoration must not compete: Chrome keeps the mode and a
    // saved offset PER HISTORY ENTRY, and that offset survives a reload of the entry, so a
    // fresh load left unscrolled came back at the previous load's offset after Back (QA
    // 2026-10-16). While the list is mounted the Feed owns the scroll; put the mode back after.
    const prevRestoration = window.history.scrollRestoration;
    window.history.scrollRestoration = "manual";
    return () => {
      window.history.scrollRestoration = prevRestoration;
      window.removeEventListener("scroll", onScroll);
      if (timer !== undefined) window.clearTimeout(timer);
      save(); // idempotent: StrictMode's fake unmount writes the same state the real one will
    };
  }, [id, entryKey]);

  const retry = useCallback(() => {
    void load();
  }, [load]);
  // Network blips leave the banner up until a reload; come back online (or tap Retry)
  // and try again without the user having to refresh the tab.
  useOnlineRetry(retry, error !== null);

  const more = async () => {
    if (!nextBefore || loadingMore) return;
    setLoadingMore(true);
    try {
      const r = await api.feed(nextBefore);
      setPosts((cur) => [...(cur ?? []), ...r.posts.filter((p) => !cur?.some((c) => c.id === p.id))]);
      setNextBefore(r.nextBefore);
    } catch {
      /* the button stays; try again */
    } finally {
      setLoadingMore(false);
    }
  };
  const update = (next: FeedPost) => setPosts((cur) => (cur ?? []).map((p) => (p.id === next.id ? next : p)));
  const drop = (pid: string) => setPosts((cur) => (cur ?? []).filter((p) => p.id !== pid));

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-6 px-4 py-6 lg:flex-row lg:items-start">
      <main className="min-w-0 flex-1 lg:max-w-2xl">
        <div className="mb-3 flex items-center gap-2">
          <h1 className="text-xs font-semibold uppercase tracking-wide text-zinc-400">{id ? "Post" : "The Feed"}</h1>
          <GuideInfoButton onClick={guide.openInfo} label="How the Feed works" />
          {id ? (
            <Link to="/feed" className="ml-auto text-xs text-zinc-400 hover:text-zinc-100">
              ← Back to the Feed
            </Link>
          ) : null}
        </div>
        <div className="flex flex-col gap-4">
          {!id ? <FeedComposer onPosted={(p) => setPosts((cur) => [p, ...(cur ?? [])])} /> : null}
          {error ? (
            <div className="flex items-center justify-between gap-3 rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-300">
              <span>{error}</span>
              <button
                type="button"
                onClick={retry}
                className="shrink-0 rounded-md border border-red-500/40 px-2.5 py-1 text-xs font-medium text-red-200 transition-colors hover:bg-red-500/20"
              >
                Retry
              </button>
            </div>
          ) : null}
          {posts === null ? (
            <p className="py-10 text-center text-sm text-zinc-500">Loading the Feed…</p>
          ) : posts.length === 0 && !error ? (
            <p className="py-10 text-center text-sm text-zinc-500">Nobody has posted a status yet. Be the first.</p>
          ) : (
            posts.map((p, i) => (
              <Fragment key={p.id}>
                <PostCard post={p} onChange={update} onDeleted={drop} openThread={!!id} eager={i === 0} />
                {/* Once per page, after the third post: below the fold on every width, so
                    nothing above it moves when it resolves. Never on a single post's page. */}
                {i === 2 && !id ? (
                  <AdSlot slot="feedInFeed" size={{ width: 300, height: 250 }} className="self-center" />
                ) : null}
              </Fragment>
            ))
          )}
          {nextBefore ? (
            <Button className="self-center" disabled={loadingMore} onClick={() => void more()}>
              {loadingMore ? "Loading…" : "Load more"}
            </Button>
          ) : null}
        </div>
      </main>
      {/* Below lg the boards sit under the posts. Shown while the posts load, they sat in
          view and were pushed down the screen when the posts arrived (CLS 0.30 on a phone),
          so below lg they wait for the posts. They stay mounted, so they still fetch in the
          meantime; at lg they sit beside the feed and nothing changes. */}
      <aside className={`${posts === null ? "hidden" : "flex"} w-full shrink-0 flex-col gap-3 lg:flex lg:w-[300px]`}>
        <Leaderboards />
      </aside>
      {guide.source ? <SurfaceGuide surface="feed" source={guide.source} onClose={guide.close} /> : null}
    </div>
  );
}
